// fx contour outline — the UNION-SILHOUETTE stroke for see-through letter
// effects (Hollow, Trace — the `contourOutline` def marker family).
//
// THE PROBLEM (owner 2026-07-20, the hollow/beveled "Subway" screenshot:
// "letter tracing needs work - we should not have the internal lines in the
// a, b and y"): fonts compose glyphs from OVERLAPPING subpaths — a bowl plus
// a stem, a diagonal crossing a diagonal. A FILLED render hides the overlaps
// (paint is idempotent), but Hollow and Trace render fill-less STROKED
// letters, and fabric strokes the RAW glyph path — every construction seam
// inside a/b/y/w reads as an internal line through the letter.
//
// THE FIX: stroke the glyph's TRUE UNION silhouette — the outer contour plus
// genuine counters only. Pipeline, per full render:
//
//   filled-glyph variant (env.renderVariant('filledSolid') — the seam forces
//   an opaque solid fill and suppresses stroke/backgrounds/decorations; the
//   union of the subpaths IS its alpha)
//     → ALPHA FIELD at the need-driven working resolution (see the
//       RESOLUTION LAW below — the tier follows the render's DELIVERED
//       output resolution, not a fixed cap)
//     → loop TOPOLOGY via the marching-squares boundary walk on the >127
//       support (outers AND holes; winding irrelevant — we stroke, never
//       fill)
//     → SUB-PIXEL EDGE EXTRACTION (smoothness law, owner 2026-07-20
//       "outlines are not smooth. Lexend is a smooth font"): every walk
//       vertex is re-placed on the alpha field's iso-0.5 CROSSING — the
//       classic sub-pixel marching-squares interpolation, adapted to the
//       boundary walk (math at subPixelRefine). Kills the lattice staircase
//       that read as wobble under the bevel's specular.
//     → one corner-pinned smoothing pass (windowed [1,2,1]/4 along the
//       contour, vertices turning ≥45° over a ±2-vertex support are pinned —
//       constants + reasoning at smoothClosedPinned)
//     → Douglas–Peucker at an OUTPUT-NEED eps: 0.75 px at the DELIVERED
//       resolution, expressed at working res as 0.75·k (k = work/need),
//       floored at the extraction-noise floor — the glyphTrace error law,
//       under a point budget with the plate's ×1.6 escalation
//     → polylines in OBJECT UNITS (frame-mapped, so full renders, region
//       patches and exports all draw the same geometry)
//     → ONE ctx.stroke() through the object's OWN live stroke props: paint
//       via fabric's Text._setStrokeStyles (Hollow's fillRouting-routed
//       texture Pattern / Gradient anchor exactly like the native stroke —
//       same handleFiller law, same object-unit ctx space), width =
//       obj.strokeWidth (fabric text ignores strokeUniform at paint time —
//       verified 6.9.1 _renderTextStroke/_renderChar — so no compensation,
//       parity with the raw-path look), dashes = obj.strokeDashArray via
//       ctx.setLineDash (Trace's ×u dash law), joins forced round (a DP
//       polyline must never miter-spike).
//
// RESOLUTION LAW (smoothness rework, the house need-driven law): need = the
// delivered raster's long edge (env.width/height — this offscreen IS what
// the render ships). k = min(1, capLong/need, renderBounds.side/need), then
// the area guard scales k down past the mode's area cap. The sync 'fast'
// pass keeps a small cap — the established soft-for-a-beat grammar, the
// debounced full-quality bake replaces it; 'full' and export track the need
// up to their caps, and a shortfall past ~2× the error target logs once
// with numbers (no-silent-caps law; the fast tier is the documented beat,
// never warned). DP eps rides k, so the DELIVERED chord error stays ≤
// ~TRACE_ERR_OUT_PX wherever the tier meets the need.
//
// A single stroke() call also fixes a subtle raw-path artifact for free:
// translucent stroke paint no longer double-darkens where neighboring bands
// overlap (native strokeText runs per char run; one path strokes once).
//
// DASH PHASE, honestly: the dash RHYTHM is identical (object-unit lengths ×u)
// but the phase origin is the union loop's walk start, not each char's path
// start — dashes land at different spots along the letter than the raw
// render. The gaps being even and fontSize-proportional is the contract;
// phase never was.
//
// DEGRADE SPLIT (the sticker-ring precedent): the effect defs keep deriving
// the native stroke props — a stock fabric consumer renders the raw-path
// stroke, seams and all, as the portable approximation. This module only
// supersedes it visually wherever the live seam runs (editor + exports).
// Degrades within the live app: a region/patch bake with no remembered
// contours, a blank silhouette, or any throw returns false and the caller
// re-renders the raw-path stroke for that pass.
//
// CACHE: WeakMap by live object (the DOME_MEMORY / sticker-MEM precedent —
// no instance props, entries die with the object). Key splits in two:
//   sil — the SILHOUETTE inputs (text, fonts, layout, wrap box, path/curve,
//         font-affecting per-char styles) + CONTOUR_OUTLINE_VERSION. Stroke
//         props are deliberately EXCLUDED: thickness/dash/color scrubs
//         re-stroke cached contours without re-tracing (the hot slider path
//         costs ~1ms).
//   res — the working dims AND the need-derived eps (`WxHeNNN`): the same
//         working raster serving a higher delivered need carries a tighter
//         eps, so the key moves with both. Full renders re-trace when
//         either half moves; REGION renders (viewport patches) reuse any
//         standing contours whose sil matches — tracing a clipped slice
//         would walk fake loops along the region border (the fxStickerShape
//         remembered-ink precedent, same frame-mapping reuse).
//
// BOOT RULE: the walker + DP below are COPIED from text3d/plateGeometry.js
// (the source of truth for the algorithm), not imported — plateGeometry
// statically imports three.js, which must never ride the editor boot chunk
// (fxRender loads this module at boot). Same copy-not-import posture, and
// the same provenance argument, as fxBevel's embossRecipe port.
//
// COST (reasoned from the plateGeometry/glyphTrace numbers — no measuring in
// this lane): the raster-linear work (downsample drawImage + getImageData +
// binarize + row scan) runs ~4-7ms per Mpx; the walk, sub-pixel refine,
// smoothing and DP are PERIMETER-linear (tens of thousands of steps at most
// — low single-digit ms). Tiers: sync 'fast' ≤1024 long edge (~0.4Mpx at a
// typical 800×300 headline ≈ 3-6ms all-in — inside the 12ms sync budget
// next to fxBevel's half-res EDT), async 'full' ≤3072 (a 2:1 headline ≈
// 4.7Mpx ≈ 20-35ms, debounced off the typing path — the 150ms upgrade
// grammar absorbs it), export ≤6144 (≈19Mpx ≈ 75-130ms once per object per
// export; the plate lane ships the same class at its 4800 print tier, with
// the same ~75-90MB transient ImageData order). Cache hits (param scrubs,
// zoom hops within a tier) skip the trace entirely.

// UNIVERSAL-EFFECTS U1 (2026-07-21): this module now ALSO registers the
// 'contourBand' fx FILTER step — Outline/Hollow/Trace for shapes and icons
// (the capability matrix's fx-contour cells). Same tracer, same cache, same
// degrade posture; the band's paint comes from STEP PARAMS (recipe units)
// instead of the object's live stroke props, because non-text kinds never
// surrender their native stroke to a slot effect (the Border tool owns
// shape edges; images have no native band). The silhouette source is the
// seam's object-silhouette variant (renderVariant — see fxRegistry's
// contract notes): shape geometry filled-solid / icon raster alpha.

// MULTI-RING OUTLINES (Depth Everywhere N3, 2026-07-22): the outline band
// family gains `rings: 1..4` + `ringGap` + `ringColor2` — the retro
// stacked-sticker look. Ring 1 IS today's band, rendered by today's code
// path byte-for-byte (the rings=1 visual-parity law: every new branch below
// is gated behind rings > 1, so a rings-absent or rings:1 recipe cannot
// reach new code). Rings 2..N are EXTRA concentric bands OUTSIDE ring 1:
//   ring k center = ring-1 band center + (k−1)·(bandWidth + gap), so the
//   EDGE-to-EDGE gap between consecutive bands is exactly `ringGap` recipe
//   units (× RING_GAP_FACTOR·u — the outline-band factor, resize-stable).
// Colors ALTERNATE between the band's own paint (odd rings — ring 3 repeats
// ring 1's paint, live stroke props / step color) and `ringColor2` (even
// rings). Default ringColor2 = '#ffffff', the die-cut sticker white — the
// honest retro default (a "poster-background contrast" default is not
// knowable from a pure def/sanitize context, and white IS the classic
// stacked-sticker edge). Trace's dashes ride EACH ring along its own path
// (fresh beginPath + setLineDash per ring — dash rhythm per contour, phase
// per ring's own walk start, same honesty note as the base band).
//
// GEOMETRY: exact offsets of the traced union contours via
// lib/geo/exactOffset.offsetRings (Clipper2/manifold — WASM). The import is
// LAZY (dynamic import on first rings>1 render — the geo chunk must never
// ride the editor boot chunk, the manifoldCore boot law) and the offsets are
// ASYNC: a render pass that finds geometry pending/missing draws ring 1 only
// (never block, never break the base band) and repaints when the bake lands
// (obj.dirty + requestRenderAll — the async-upgrade grammar). Geometry is
// cached per object in RING_MEMORY keyed by silhouette sig + ring deltas;
// a res-tier move (zoom hop, export) serves the standing loops for that pass
// and re-offsets against the freshly traced contours in the background.
// FALLBACK LAW: when the geo module fails to load (offline chunk, WASM
// unavailable) rings degrade to 1 — today's band exactly — and the failure
// logs once. Region/patch bakes never kick geometry (the contour region-
// reuse law); they draw whatever ready rings the full render left.
//
// TEXT OUTLINE (native-stroke family): hollow/trace ride renderContourOutline
// below, but the Outline effect's ring 1 is fabric's own native stroke
// (derive props — untouched, parity by construction). Its outer rings paint
// through renderOutlineOuterRings, called by the seam AFTER the die-cut
// closing (the closing must never read ring gaps as pockets to bridge) and
// before the filter chain (a bevel domes rings like any band ink). The
// visible band width there is strokeWidth/2 for position:'outside'
// (paintFirst==='stroke' — the def doubles the width and hides the inner
// half under the fill) and strokeWidth for 'centered'.
import { registerFx } from './fxRegistry';
import { objectKindOf, effectsBagOf } from './capabilityMatrix';
import { EFFECT_DEFS } from '../../inspector/text/effectDefs.js';

// ---------------------------------------------------------------- constants
// v2 (smoothness rework, owner 2026-07-20 "outlines are not smooth. Lexend
// is a smooth font"): sub-pixel iso-0.5 extraction + corner-pinned
// smoothing + need-driven working res + output-need DP eps. v1's lattice-
// snapped contours invalidate via the version in the sil key.
export const CONTOUR_OUTLINE_VERSION = 2;

// Working-resolution ceilings (long edge, px) per render mode — the NEED
// (delivered raster) is the target, these bound the cost. Also clamped by
// env.renderBounds.side (runtime renderCaps ∩ fabric config for the mode)
// and the area caps (pathological aspects).
const TRACE_CAP_FAST = 1024; // the soft-for-a-beat sync tier
const TRACE_CAP_FULL = 3072; // debounced async upgrade
const TRACE_CAP_EXPORT = 6144; // once per object per export
const TRACE_AREA_FAST = 1.4e6;
const TRACE_AREA_FULL = 10e6;
const TRACE_AREA_EXPORT = 26e6;

// Error law (glyphTrace's silhouette budget): chord-error target at the
// DELIVERED resolution. DP eps at working res = TRACE_ERR_OUT_PX × k
// (k = work/need ≤ 1) — constant 0.75 px in DELIVERED space — floored at
// the sub-pixel extraction noise floor and capped at glyphTrace's 2.6. The
// old 0.8 lattice-px floor existed because binary walk points could not BE
// smoother than the staircase; iso-0.5 points can, so the floor drops to
// the ~0.3 px the extraction itself is good for (below it DP would keep
// extraction jitter as vertices for nothing).
const TRACE_ERR_OUT_PX = 0.75;
const DP_EPS_MIN_WORK_PX = 0.3;
const DP_EPS_MAX_WORK_PX = 2.6;
// Shortfall note threshold: estimated delivered chord error ≈ (extraction
// ~0.3 + eps)/k — logged once (full/export tiers only) past 2× the target.
const SHORTFALL_NOTE_X = 2;

// Corner-pin threshold for the smoothing pass — see smoothClosedPinned.
const CORNER_PIN_DEG = 45;
const CORNER_PIN_COS = Math.cos((CORNER_PIN_DEG * Math.PI) / 180);

const POINT_BUDGET_BASE = 6000; // at the 1024 tier
const POINT_BUDGET_HARD = 18000;
const MIN_CONTOUR_POINTS = 8; // single-px dust is dropped (plate law)

// Per-char style keys that move the letterform silhouette (mirrors the
// seam's BAKED_STYLE_KEYS minus 'fill' — content COLORS are irrelevant to
// alpha; a hand-edited transparent per-char fill is the one documented hole,
// and the UI blocks per-char fills under hollow/trace anyway).
const SIL_STYLE_KEYS = ['fontFamily', 'fontWeight', 'fontStyle', 'fontSize', 'deltaY'];

// Multi-ring constants (header note). RING_GAP_FACTOR mirrors the outline
// band's 0.25 recipe factor so "Ring spacing" and "Thickness" speak the same
// scale; RING_COLOR2_DEFAULT is the die-cut sticker white (the honest retro
// default — see header).
export const RING_MAX = 4;
const RING_GAP_FACTOR = 0.25;
const RING_COLOR2_DEFAULT = '#ffffff';

/** Normalize the ring param trio from any raw params bag (effect entry or
 * step params). Absent/garbage input reads as rings 1 — today's band. */
function ringParamsOf(src) {
  const n = Math.round(Number(src && src.rings));
  const rings = Number.isFinite(n) ? Math.min(RING_MAX, Math.max(1, n)) : 1;
  const g = Math.round(Number(src && src.ringGap));
  const ringGap = Number.isFinite(g) ? Math.min(100, Math.max(1, g)) : 25;
  const c2 = src && src.ringColor2;
  const ringColor2 = typeof c2 === 'string' && c2 ? c2 : RING_COLOR2_DEFAULT;
  return { rings, ringGap, ringColor2 };
}

// ---------------------------------------------------------------- stats/log
const stats = {
  traces: 0, // fresh union traces (cache misses)
  cacheHits: 0, // full renders served from the standing contours
  regionReuses: 0, // region/patch bakes drawn from remembered contours
  degrades: 0, // renders that fell back to the raw-path stroke
  lastTraceMs: 0,
  lastPoints: 0, // simplified points across contours of the last trace
  lastLoops: 0, // contour loops (outers + counters) of the last trace
  lastPinned: 0, // corner-pinned vertices of the last trace (smoothing law)
  lastEps: 0, // DP eps (working px) actually used by the last trace
  lastWorkLong: 0, // working long edge of the last trace
  lastNeedLong: 0, // delivered-need long edge of the last trace
  ringBakes: 0, // completed offset-ring geometry bakes (multi-ring lane)
  ringFallbacks: 0, // rings>1 renders served ring-1-only (pending/unavailable)
  lastRingsDrawn: 0, // outer rings actually stroked by the last band render
};

/** Dev/probe surface — exposed via window.__cceFx.contour (fxRender). */
export function getContourOutlineStats() {
  return { ...stats };
}

const loggedOnce = new Set();
function logOnce(key, err) {
  if (loggedOnce.has(key)) return;
  loggedOnce.add(key);
  // eslint-disable-next-line no-console
  console.warn(`[fx contourOutline] ${key}`, err || '');
}

// ---------------------------------------------------------------- walker
// COPIED from text3d/plateGeometry.js (traceBinaryContours) — see the BOOT
// RULE note above. Directed walk on the pixel lattice: pixels are unit
// squares, boundary loops are chains of lattice edges between a filled and
// an empty pixel, walked with the FILLED side on the right. Every loop
// (outer boundaries AND holes) contains at least one "filled below, empty
// above" edge, so one row scan finds them all; a directed-step visited set
// makes each loop trace once. Screen coords (y down). Since the smoothness
// rework the walk supplies loop TOPOLOGY only — vertex POSITIONS are
// re-derived sub-pixel by subPixelRefine below.
const DX = [1, 0, -1, 0];
const DY = [0, 1, 0, -1];

function traceBinaryContoursLocal(bin, W, H) {
  const filled = (x, y) => x >= 0 && y >= 0 && x < W && y < H && bin[y * W + x] !== 0;
  const stepKey = (cx, cy, d) => (cy * (W + 1) + cx) * 4 + d;
  const visited = new Set();
  const contours = [];

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (!filled(x, y) || filled(x, y - 1)) continue; // want filled with empty above
      if (visited.has(stepKey(x, y, 0))) continue;
      const pts = [];
      let cx = x;
      let cy = y;
      let d = 0;
      for (let guard = 0; guard < W * H * 4 + 8; guard++) {
        visited.add(stepKey(cx, cy, d));
        pts.push([cx, cy]);
        cx += DX[d];
        cy += DY[d];
        const ld = (d + 3) % 4;
        const alx = cx + (DX[d] + DX[ld] - 1) / 2;
        const aly = cy + (DY[d] + DY[ld] - 1) / 2;
        const rd = (d + 1) % 4;
        const arx = cx + (DX[d] + DX[rd] - 1) / 2;
        const ary = cy + (DY[d] + DY[rd] - 1) / 2;
        if (filled(alx, aly)) d = ld; // inner corner → turn left
        else if (filled(arx, ary)) { /* straight */ }
        else d = rd; // outer corner → turn right
        if (cx === x && cy === y && d === 0) break; // loop closed
      }
      if (pts.length >= MIN_CONTOUR_POINTS) contours.push(pts);
    }
  }
  return contours;
}

// ---------------------------------------------------------------- sub-pixel
/**
 * ISO-0.5 CROSSING REFINEMENT (smoothness law). The walk emits integer
 * lattice corners; each consecutive pair spans one boundary lattice edge
 * separating a FILLED pixel (alpha > 127, on the travel direction's right)
 * from an EMPTY one (left). That edge is exactly the dual-grid crossing of
 * classic marching squares — so re-place its vertex by linearly
 * interpolating the alpha field between the two flanking PIXEL CENTERS and
 * solving for the 127.5 iso level:
 *
 *   u axis ⟂ edge: u = −½ at the filled center, +½ at the empty center,
 *   0 on the lattice edge.  a(u) = aIn + (u + ½)(aOut − aIn)
 *   a(u*) = 127.5  ⇒  u* = (127.5 − aIn)/(aOut − aIn) − ½ ∈ [−½, +½]
 *   vertex = edgeMidpoint − u*·r̂   (r̂ = unit normal toward the FILLED side,
 *   r̂ = (−dy, dx) of the travel direction in y-down coords — the walk law,
 *   so −u*·r̂ moves toward the empty side for positive u*)
 *
 * One vertex per boundary edge (same count as the corner walk). A crisp
 * edge (aIn 255 / aOut 0) lands u* = 0 — the lattice edge, the old
 * behavior; an AA edge moves the vertex onto the coverage-0.5 line.
 * Residual error, stated honestly: where one flanking sample saturates
 * (true edge > ½ px from that pixel's center) the coverage clamp breaks
 * strict linearity — worst-case bias ~0.15 work px, varying SLOWLY along
 * the contour (reads as smooth), versus the ±0.5 px staircase this
 * replaces: a ~4-7× ripple reduction at any tier before the smoothing pass
 * even runs. aIn > 127 ≥ aOut holds by the walk's own definition, so the
 * denominator is always negative; out-of-bounds samples read 0 (the seam's
 * offscreen pads glyph ink and regions never trace — belt only).
 */
function subPixelRefine(pts, alpha, W, H) {
  const n = pts.length;
  const out = new Array(n);
  const sample = (x, y) => (x >= 0 && y >= 0 && x < W && y < H ? alpha[y * W + x] : 0);
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const mx = a[0] + dx / 2;
    const my = a[1] + dy / 2;
    // Right of travel (y-down) = the filled side: r = (−dy, dx).
    const rx = -dy;
    const ry = dx;
    const aIn = sample(Math.round(mx + 0.5 * rx - 0.5), Math.round(my + 0.5 * ry - 0.5));
    const aOut = sample(Math.round(mx - 0.5 * rx - 0.5), Math.round(my - 0.5 * ry - 0.5));
    let u = 0;
    const denom = aOut - aIn;
    if (denom < 0) {
      u = (127.5 - aIn) / denom - 0.5;
      if (u < -0.49) u = -0.49;
      else if (u > 0.49) u = 0.49;
    }
    out[i] = [mx - u * rx, my - u * ry];
  }
  return out;
}

// ---------------------------------------------------------------- smoothing
/**
 * ONE corner-pinned windowed pass along the closed contour (smoothness law):
 * out[i] = ¼·v[i−1] + ½·v[i] + ¼·v[i+1], except PINNED vertices keep their
 * exact position. Purpose: kill the residual, spatially-uncorrelated
 * binarization/AA noise the sub-pixel lerp leaves behind (≤~0.15 work px)
 * WITHOUT rounding true letterform corners.
 *
 * Windowed average over Chaikin, deliberately: Chaikin doubles the point
 * count per iteration (fights the DP budget for nothing) and cuts EVERY
 * corner by construction — pinning would have to fight its own kernel. The
 * [1,2,1]/4 kernel is budget-neutral, attenuates single-vertex noise ~2×,
 * and displaces a genuinely smooth arc only by its local sagitta: at ~1
 * work-px vertex spacing and letter curvature radii ≥ ~15-20 work px, the
 * systematic inward bias is ≤ ~0.01 work px — negligible against the 0.3 px
 * eps floor. ONE iteration only (repeated averaging is curve shrinkage).
 *
 * CORNER PRESERVATION — constants reasoned, since eyes are not allowed in
 * this lane: a vertex is pinned when the contour direction turns ≥
 * CORNER_PIN_DEG (45°) across it, measured over a ±2-VERTEX SUPPORT
 * (v[i]−v[i−2] against v[i+2]−v[i]):
 *   - 45° sits inside the tessellation lane's crease band (plateGeometry
 *     creases normals at 50°; the stated sensibility is 40-50°): below it
 *     is "smooth curve", above it is "corner".
 *   - Noise cannot trip it: ≤~0.15 px jitter over ~1 px spacing bends the
 *     single-edge direction ≤ ~2·atan(0.15/1) ≈ 17° worst case, and the ±2
 *     support (~4 px chords) roughly halves that — comfortable margin.
 *   - True corners always trip it: stem/bar junctions and terminals turn
 *     60-90°+, and even when rasterization spreads a 90° corner across two
 *     adjacent vertices (~45° each), the ±2 support spans the WHOLE corner
 *     and reads the full turn at its center vertex (a same-support single
 *     -edge measure would see only the half-turn and smooth it away).
 *   - Smooth glyph curves never trip it: over ~4 px of arc a curve of
 *     radius R turns ~4/R rad ≈ ≤15° for the tightest bowls that survive at
 *     working res (R ≥ ~15 px) — ≪ 45°.
 * Pinned counts ride stats.lastPinned (probe sanity: a geometric sans pins
 * a few dozen vertices; a slab serif pins many).
 */
function smoothClosedPinned(pts) {
  const n = pts.length;
  if (n < MIN_CONTOUR_POINTS) return { pts: pts.slice(), pinned: 0 };
  const out = new Array(n);
  let pinnedCount = 0;
  for (let i = 0; i < n; i++) {
    const pm2 = pts[(i - 2 + n) % n];
    const pm1 = pts[(i - 1 + n) % n];
    const p0 = pts[i];
    const pp1 = pts[(i + 1) % n];
    const pp2 = pts[(i + 2) % n];
    const ax = p0[0] - pm2[0];
    const ay = p0[1] - pm2[1];
    const bx = pp2[0] - p0[0];
    const by = pp2[1] - p0[1];
    const la = Math.hypot(ax, ay);
    const lb = Math.hypot(bx, by);
    let pin = false;
    if (la > 1e-9 && lb > 1e-9) {
      const cos = (ax * bx + ay * by) / (la * lb);
      pin = cos <= CORNER_PIN_COS; // turn ≥ CORNER_PIN_DEG over the support
    }
    if (pin) {
      pinnedCount += 1;
      out[i] = [p0[0], p0[1]];
    } else {
      out[i] = [
        0.25 * pm1[0] + 0.5 * p0[0] + 0.25 * pp1[0],
        0.25 * pm1[1] + 0.5 * p0[1] + 0.25 * pp1[1],
      ];
    }
  }
  return { pts: out, pinned: pinnedCount };
}

// ---------------------------------------------------------------- DP
// COPIED from text3d/plateGeometry.js (simplifyClosed + helpers) — BOOT RULE.
function perpDistSq(p, a, b) {
  const vx = b[0] - a[0];
  const vy = b[1] - a[1];
  const wx = p[0] - a[0];
  const wy = p[1] - a[1];
  const len = vx * vx + vy * vy;
  if (len < 1e-12) return wx * wx + wy * wy;
  const t = Math.max(0, Math.min(1, (wx * vx + wy * vy) / len));
  const dx = wx - t * vx;
  const dy = wy - t * vy;
  return dx * dx + dy * dy;
}

function dpOpen(pts, lo, hi, epsSq, keep) {
  if (hi <= lo + 1) return;
  let maxD = -1;
  let maxI = -1;
  for (let i = lo + 1; i < hi; i++) {
    const dist = perpDistSq(pts[i], pts[lo], pts[hi]);
    if (dist > maxD) { maxD = dist; maxI = i; }
  }
  if (maxD > epsSq) {
    keep[maxI] = 1;
    dpOpen(pts, lo, maxI, epsSq, keep);
    dpOpen(pts, maxI, hi, epsSq, keep);
  }
}

function simplifyClosedLocal(pts, eps) {
  const n = pts.length;
  if (n <= 4) return pts.slice();
  let far = 1;
  let farD = -1;
  for (let i = 1; i < n; i++) {
    const dx = pts[i][0] - pts[0][0];
    const dy = pts[i][1] - pts[0][1];
    const dist = dx * dx + dy * dy;
    if (dist > farD) { farD = dist; far = i; }
  }
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[far] = 1;
  const epsSq = eps * eps;
  dpOpen(pts, 0, far, epsSq, keep);
  const tail = [];
  const map = [];
  for (let i = far; i < n; i++) { tail.push(pts[i]); map.push(i); }
  tail.push(pts[0]);
  map.push(0);
  const keep2 = new Uint8Array(tail.length);
  keep2[0] = 1;
  keep2[tail.length - 1] = 1;
  dpOpen(tail, 0, tail.length - 1, epsSq, keep2);
  for (let i = 1; i < tail.length - 1; i++) if (keep2[i]) keep[map[i]] = 1;
  const out = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(pts[i]);
  return out;
}

// ---------------------------------------------------------------- cache
// obj → { sil, res, contours } — contours in OBJECT UNITS (center-origin,
// the space env.frame maps), so any later frame (full re-render in the same
// tier, region patch, export crop) draws them by transform alone.
const CONTOUR_MEMORY = new WeakMap();

/** Non-text silhouette signature (universal-effects U1): the inputs that
 * move a shape's geometry or an icon's raster alpha. Live paint (fill/
 * stroke/tint) is deliberately absent — the silhouette variant parks paint,
 * and the isIcon tint pipeline changes color, never alpha (a tint recolor
 * REPLACES the instance anyway, which starts a fresh WeakMap entry). */
function objectSilhouetteSigOf(obj) {
  const el = obj._originalElement || obj._element;
  const src = (el && (el.currentSrc || el.src)) || '';
  return [
    'obj', CONTOUR_OUTLINE_VERSION, obj.type ?? '',
    Math.round(obj.width || 0), Math.round(obj.height || 0),
    obj.rx || 0, obj.ry || 0, obj.radius || 0,
    Array.isArray(obj.points) ? obj.points.length : 0,
    Array.isArray(obj.path) ? obj.path.length : 0,
    obj.cropX || 0, obj.cropY || 0,
    src.length, src.slice(0, 48),
  ].join('|');
}

/** Silhouette signature: everything that moves the union alpha, NOTHING that
 * only moves the band's paint (see the cache note in the header). */
function silhouetteSigOf(obj) {
  const kind = objectKindOf(obj);
  if (kind === 'shape' || kind === 'icon' || kind === 'photo') {
    return objectSilhouetteSigOf(obj);
  }
  let stylesSig = '';
  try {
    const styles = obj.styles || {};
    for (const li of Object.keys(styles)) {
      const line = styles[li];
      if (!line) continue;
      for (const ci of Object.keys(line)) {
        const decl = line[ci];
        if (!decl) continue;
        let sig = '';
        for (const key of SIL_STYLE_KEYS) {
          const v = decl[key];
          if (v === undefined) continue;
          sig += `${key}=${typeof v === 'string' || typeof v === 'number' ? v : 'o'},`;
        }
        if (sig) stylesSig += `${li}:${ci}:${sig};`;
      }
    }
  } catch (_) { stylesSig = '?'; }
  return [
    CONTOUR_OUTLINE_VERSION,
    obj.text ?? '', obj.fontFamily ?? '', obj.fontWeight ?? '', obj.fontStyle ?? '',
    obj.fontSize ?? '', obj.lineHeight ?? '', obj.charSpacing ?? '', obj.textAlign ?? '',
    obj.direction ?? '',
    Math.round(obj.width || 0), Math.round(obj.height || 0),
    obj.curveAmount || 0, obj.path ? 1 : 0, obj.pathStartOffset || 0, obj.pathSide ?? '',
    stylesSig,
  ].join('');
}

// ---------------------------------------------------------------- rings geo
// obj → { key, res, status: 'pending'|'ready'|'failed', ringLoops, promise }.
// ringLoops = one house ring-set per OUTER ring (index 0 = ring 2), object
// units, straight from offsetRings. Same WeakMap lifecycle as
// CONTOUR_MEMORY: entries die with the object, no instance props.
const RING_MEMORY = new WeakMap();

let ringGeoPromise = null; // the lazy exactOffset module (WASM chunk)
let ringGeoBroken = false; // import failed once → rings=1 for the session

function loadRingGeo() {
  if (!ringGeoPromise) {
    ringGeoPromise = (async () => {
      // Emscripten resolves manifold.wasm relative to the SERVED module URL,
      // which 404s under bundlers (vite dep-optimized chunk has no sibling
      // .wasm). Pre-warm F1's singleton through its locateFile escape hatch
      // with the bundler's own emitted asset URL — the liveExtrude
      // precedent, byte-identical mapping so whichever lane initializes
      // first serves both. Everything here is lazy: the ?url import is a
      // tiny string chunk, the WASM only ever loads on a rings>1 bake.
      const [core, wasmUrl] = await Promise.all([
        import('../geo/manifoldCore.js'),
        import('manifold-3d/manifold.wasm?url').then((m) => m.default),
      ]);
      await core.getManifold({
        locateFile: (p) => (String(p).endsWith('.wasm') ? wasmUrl : p),
      });
      return import('../geo/exactOffset.js');
    })();
  }
  return ringGeoPromise;
}

/** Probe/dev hook: true forces the geo-unavailable degrade (rings=1) without
 * touching real module state — the fallback law's test switch. */
function ringGeoDisabled() {
  return typeof window !== 'undefined' && window.__cceRingGeoDisabled === true;
}

/**
 * Standing-or-kicked offset-ring geometry for `deltas` (object units,
 * measured from the ring-1 band CENTER, one per outer ring). Returns the
 * ready per-ring loop sets, or null (pending / unavailable / patch bake
 * with nothing standing) — the caller then draws ring 1 only for this pass
 * (the fallback law). `allowKick` false = region/patch posture: reuse only.
 *
 * A res-tier move (`res` differs while sil+deltas match) serves the standing
 * loops for THIS pass and re-offsets against the fresh contours behind it —
 * the soft-for-a-beat upgrade grammar. Positional error of the stale serve
 * is bounded by the old tier's trace eps (sub-px at its own delivered res);
 * exports log the stale serve once (no-silent-caps posture).
 */
function ensureRingLoops(env, contours, deltas, sil, res, allowKick) {
  const obj = env.obj;
  const key = `${sil}§${deltas.map((d) => d.toFixed(3)).join(',')}`;
  const mem = RING_MEMORY.get(obj);
  if (mem && mem.key === key) {
    if (mem.status === 'ready') {
      if (mem.res !== res && allowKick && !ringGeoDisabled() && !ringGeoBroken) {
        if (env.exporting) {
          logOnce(`ring geometry served from the ${mem.res} tier for a ${res} export — `
            + 'sub-px stale for this pass; re-offset kicked');
        }
        kickRingBake(obj, contours, deltas, key, res, mem.ringLoops);
      }
      return mem.ringLoops;
    }
    return null; // pending or failed — ring 1 only this pass
  }
  if (!allowKick) return null; // patch bake, nothing standing — region-reuse law
  if (ringGeoDisabled() || ringGeoBroken) {
    // No entry stored: renders stay ring-1 while unavailable (callers count
    // the fallback), and the kick resumes by itself the moment the geo is
    // available again — the degrade must never outlive its cause.
    return null;
  }
  kickRingBake(obj, contours, deltas, key, res, null);
  return null;
}

/** Start (or supersede with) an async offset bake; keeps `carry` (the
 * previous ready loops) servable until the new bake lands. */
function kickRingBake(obj, contours, deltas, key, res, carry) {
  const entry = {
    key,
    res,
    status: carry ? 'ready' : 'pending',
    ringLoops: carry,
    promise: null,
  };
  RING_MEMORY.set(obj, entry);
  entry.promise = (async () => {
    let geo;
    try {
      geo = await loadRingGeo();
    } catch (e) {
      ringGeoBroken = true; // chunk unreachable — stop retrying this session
      throw e;
    }
    const out = [];
    for (const d of deltas) {
      // Positive delta = everywhere-away-from-ink under the EvenOdd law
      // (outers grow, counters shrink; a counter too small for ring k simply
      // vanishes there — honest). Adjacent letters' rings union per ring.
      out.push(await geo.offsetRings(contours, d, { join: 'round' }));
    }
    if (RING_MEMORY.get(obj) !== entry) return; // superseded — drop silently
    entry.ringLoops = out;
    entry.status = 'ready';
    stats.ringBakes += 1;
    // Async-upgrade grammar: repaint the object now that rings exist. The
    // object may have died between kick and land (undo rebuild, removal,
    // canvas dispose) — a repaint on a stale instance must never throw into
    // an unhandled rejection, and a dead object simply keeps its geometry
    // in the WeakMap until GC takes both.
    try {
      obj.dirty = true;
      if (obj.group) obj.group.dirty = true;
      const cv = obj.canvas;
      if (cv && cv.lowerCanvasEl && typeof cv.requestRenderAll === 'function') {
        cv.requestRenderAll();
      }
    } catch (_) { /* stale instance — the geometry still serves reloads */ }
  })();
  entry.promise.catch((e) => {
    if (RING_MEMORY.get(obj) !== entry) return;
    entry.status = carry ? 'ready' : 'failed'; // keep serving the carry
    stats.ringFallbacks += 1;
    logOnce('ring offset geometry failed — rings degrade to 1', e);
  });
}

// ---------------------------------------------------------------- tracing
/** The RESOLUTION LAW, wired (header note): need-driven working dims under
 * mode caps ∩ runtime caps ∩ area guard, plus the output-need DP eps. */
function traceWorkOf(env) {
  const needLong = Math.max(1, Math.max(env.width, env.height));
  const capLong = env.exporting
    ? TRACE_CAP_EXPORT
    : (env.quality === 'full' ? TRACE_CAP_FULL : TRACE_CAP_FAST);
  const capArea = env.exporting
    ? TRACE_AREA_EXPORT
    : (env.quality === 'full' ? TRACE_AREA_FULL : TRACE_AREA_FAST);
  // Runtime ceiling: renderBounds IS renderCaps ∩ fabric config for this
  // mode (the seam passes it) — the working scratch must never out-allocate
  // what the mode may hold. Belt: the base raster already respects it, so
  // need ≤ side by construction.
  const boundsSide = env.renderBounds && env.renderBounds.side
    ? env.renderBounds.side : capLong;
  let k = Math.min(1, capLong / needLong, boundsSide / needLong);
  let W = Math.max(8, Math.round(env.width * k));
  let H = Math.max(8, Math.round(env.height * k));
  if (W * H > capArea) {
    k *= Math.sqrt(capArea / (W * H));
    W = Math.max(8, Math.round(env.width * k));
    H = Math.max(8, Math.round(env.height * k));
  }
  // DP eps at WORKING res so the DELIVERED chord error ≈ TRACE_ERR_OUT_PX
  // wherever the tier meets the need (eps_delivered = eps_work / k).
  const kEff = Math.max(1e-6, Math.max(W, H) / needLong);
  const eps = Math.min(
    DP_EPS_MAX_WORK_PX,
    Math.max(DP_EPS_MIN_WORK_PX, TRACE_ERR_OUT_PX * kEff),
  );
  return { W, H, k: kEff, eps, needLong };
}

/** Trace the union contours from the filledSolid variant at working res.
 * Returns object-unit polylines, or null (blank / no variant / hiccup). */
function traceUnionContours(env) {
  const t0 = performance.now();
  const v = typeof env.renderVariant === 'function' ? env.renderVariant('filledSolid') : null;
  if (!v || !v.canvas) return null;
  const { W, H, k, eps, needLong } = traceWorkOf(env);
  const s = env.getScratch(W, H);
  s.ctx.setTransform(1, 0, 0, 1, 0, 0);
  s.ctx.clearRect(0, 0, W, H);
  s.ctx.drawImage(v.canvas, 0, 0, env.width, env.height, 0, 0, W, H);
  const data = s.ctx.getImageData(0, 0, W, H).data;
  // Keep the ALPHA FIELD (the sub-pixel lerp source) alongside the >127
  // support (the walk's topology grid).
  const alpha = new Uint8ClampedArray(W * H);
  const bin = new Uint8Array(W * H);
  let any = false;
  for (let i = 0, a = 3; i < bin.length; i++, a += 4) {
    const al = data[a];
    alpha[i] = al;
    if (al > 127) { bin[i] = 1; any = true; }
  }
  if (!any) return null; // blank text — nothing to band

  const raw = traceBinaryContoursLocal(bin, W, H);
  if (!raw.length) return null;

  // Sub-pixel placement, then ONE corner-pinned smoothing pass. Order
  // matters: smoothing removes the residual zero-mean noise BEFORE DP
  // commits vertices to it — DP then simplifies an already-smooth curve
  // (DP after a noisy extraction would keep jitter bumps as "features" at
  // the tight output-need eps).
  let pinnedTotal = 0;
  const smooth = raw.map((loop) => {
    const refined = subPixelRefine(loop, alpha, W, H);
    const sm = smoothClosedPinned(refined);
    pinnedTotal += sm.pinned;
    return sm.pts;
  });

  // Simplify under the tier's point budget (plate law: escalate eps ×1.6).
  const budget = Math.min(
    POINT_BUDGET_HARD,
    Math.round(POINT_BUDGET_BASE * Math.max(1, Math.max(W, H) / TRACE_CAP_FAST)),
  );
  let dpEps = eps;
  let simplified = smooth.map((c) => simplifyClosedLocal(c, dpEps));
  let total = simplified.reduce((n, c) => n + c.length, 0);
  for (let round = 0; total > budget && round < 4; round++) {
    dpEps *= 1.6;
    simplified = smooth.map((c) => simplifyClosedLocal(c, dpEps));
    total = simplified.reduce((n, c) => n + c.length, 0);
  }

  // No-silent-caps: when the tier could not meet the need and the estimated
  // DELIVERED chord error (extraction ~0.3 + eps, both /k) runs past 2× the
  // target, say so once with numbers. The fast tier is exempt — it is the
  // documented soft-for-a-beat pass; the full-quality bake replaces it.
  if (env.quality === 'full' || env.exporting) {
    const estOut = (0.3 + dpEps) / k;
    if (estOut > TRACE_ERR_OUT_PX * SHORTFALL_NOTE_X) {
      logOnce(`trace under-resolved: work ${Math.max(W, H)}px vs need ${needLong}px `
        + `(k ${k.toFixed(2)}, eps ${dpEps.toFixed(2)}wpx) — est. delivered chord error `
        + `~${estOut.toFixed(2)}px vs ${TRACE_ERR_OUT_PX}px target (tier/area cap bound)`);
    }
  }

  // Working px → OBJECT UNITS through the frame (full renders only reach
  // here, so the frame spans the whole offscreen): obj = frame.x0 + px/effX,
  // with px = workPx / (W/env.width).
  const kx = (W / env.width) * env.frame.effX;
  const ky = (H / env.height) * env.frame.effY;
  const contours = simplified
    .filter((c) => c.length >= 3)
    .map((c) => c.map(([px, py]) => [env.frame.x0 + px / kx, env.frame.y0 + py / ky]));

  stats.traces += 1;
  stats.lastTraceMs = performance.now() - t0;
  stats.lastPoints = total;
  stats.lastLoops = contours.length;
  stats.lastPinned = pinnedTotal;
  stats.lastEps = dpEps;
  stats.lastWorkLong = Math.max(W, H);
  stats.lastNeedLong = needLong;
  return contours;
}

/** Standing-or-fresh contours for this render. Region bakes NEVER trace (a
 * clipped slice walks fake loops along the region border) — they reuse the
 * remembered full-render contours or degrade. */
function contoursFor(env) {
  const obj = env.obj;
  if (!obj) return null;
  const sil = silhouetteSigOf(obj);
  const mem = CONTOUR_MEMORY.get(obj);
  if (env.patchRegion) {
    if (mem && mem.sil === sil && mem.contours) {
      stats.regionReuses += 1;
      return mem.contours;
    }
    logOnce('region bake with no remembered contours — raw-path stroke in this patch');
    return null;
  }
  const { W, H, eps } = traceWorkOf(env);
  // res key carries the working dims AND the need-derived eps: the same
  // working raster serving a higher delivered need must re-trace tighter
  // (need moved even though W×H capped identically).
  const res = `${W}x${H}e${Math.round(eps * 1000)}`;
  if (mem && mem.sil === sil && mem.res === res && mem.contours) {
    stats.cacheHits += 1;
    return mem.contours;
  }
  const contours = traceUnionContours(env);
  if (contours && contours.length) {
    CONTOUR_MEMORY.set(obj, { sil, res, contours });
    return contours;
  }
  return null;
}

/**
 * UNIVERSAL-EFFECTS U3 — the extrude source's silhouetteOf input for ICONS
 * (the traced half; shapes ride exact vectors in text3d/silhouette3d).
 * REUSES this module's whole extraction verbatim — the U1 silhouette
 * variant (env.renderVariant), the sub-pixel iso-0.5 walk, corner-pinned
 * smoothing, output-need DP, the WeakMap cache and the region-reuse law
 * (patch bakes reuse standing full-render contours; a patch with none
 * remembered returns null and the 3D patch honestly aborts — the base
 * stash stands). Returns object-unit center-origin y-down polylines, or
 * null (blank silhouette / no variant / hiccup — the caller degrades flat).
 */
export function traceObjectSilhouette(env) {
  try {
    if (!env || !env.obj || !env.frame) return null;
    const contours = contoursFor(env);
    return contours && contours.length ? contours : null;
  } catch (e) {
    logOnce('object silhouette trace threw — extrude degrades flat', e);
    return null;
  }
}

// ---------------------------------------------------------------- stroking
/** Stroke the union band onto `ctx` (any transform state — this sets its
 * own frame transform inside save/restore) with the object's LIVE stroke
 * props. See the header for the paint/width/dash fidelity law. */
function strokeContoursOnCtx(ctx, env, contours) {
  const obj = env.obj;
  const frame = env.frame;
  ctx.save();
  try {
    ctx.setTransform(
      frame.effX, 0, 0, frame.effY,
      -frame.x0 * frame.effX, -frame.y0 * frame.effY,
    );
    ctx.beginPath();
    for (const loop of contours) {
      ctx.moveTo(loop[0][0], loop[0][1]);
      for (let i = 1; i < loop.length; i++) ctx.lineTo(loop[i][0], loop[i][1]);
      ctx.closePath();
    }
    // Dash law: object-unit lengths under the frame transform — exactly the
    // space fabric's _renderTextStroke sets them in (Trace's ×u rhythm).
    if (Array.isArray(obj.strokeDashArray) && obj.strokeDashArray.length) {
      ctx.setLineDash(obj.strokeDashArray);
    }
    // Paint through fabric's own Text stroke styling: lineWidth/cap/offset/
    // miter + strokeStyle incl. the routed Gradient/Pattern anchoring
    // (handleFiller may translate the ctx for the paint mapping — the path
    // above is already recorded, so only the PAINT anchors move, exactly the
    // native strokeText compensation nets out to). No strokeUniform division
    // on purpose: fabric text ignores it at paint time (parity).
    if (typeof obj._setStrokeStyles === 'function') {
      obj._setStrokeStyles(ctx, obj);
    } else {
      // Non-Text carrier (defensive — the marker family is text-only today).
      ctx.lineWidth = Number(obj.strokeWidth) || 1;
      ctx.lineCap = obj.strokeLineCap || 'round';
      ctx.strokeStyle = typeof obj.stroke === 'string' ? obj.stroke : '#000000';
    }
    // DP polylines have vertices every few px — a miter join would spike at
    // reflex corners (Trace's def leaves fabric's default 'miter'). Round is
    // the band family's join law; set it AFTER _setStrokeStyles so it wins.
    ctx.lineJoin = 'round';
    ctx.stroke();
  } finally {
    ctx.restore();
  }
}

// ---------------------------------------------------------------- rings
/** Stroke ONE outer ring's loop set through the object's live stroke
 * conventions (multi-ring lane). Odd rings repeat the band's own paint
 * (ring 3 = ring 1's look — _setStrokeStyles routes Pattern/Gradient
 * exactly like the base band); even rings paint ringColor2. `widthU`
 * overrides the painted line width (the outline family's visible band is
 * half its native strokeWidth — see the header). Dashes ride each ring's
 * own path: fresh beginPath + setLineDash per call. */
function strokeTextRingOnCtx(ctx, env, loops, ringNo, ringColor2, widthU) {
  const obj = env.obj;
  const frame = env.frame;
  ctx.save();
  try {
    ctx.setTransform(
      frame.effX, 0, 0, frame.effY,
      -frame.x0 * frame.effX, -frame.y0 * frame.effY,
    );
    ctx.beginPath();
    for (const loop of loops) {
      if (!loop || loop.length < 3) continue;
      ctx.moveTo(loop[0][0], loop[0][1]);
      for (let i = 1; i < loop.length; i++) ctx.lineTo(loop[i][0], loop[i][1]);
      ctx.closePath();
    }
    if (Array.isArray(obj.strokeDashArray) && obj.strokeDashArray.length) {
      ctx.setLineDash(obj.strokeDashArray);
    }
    if (ringNo % 2 === 0) {
      ctx.lineWidth = widthU;
      ctx.lineCap = obj.strokeLineCap || 'round';
      ctx.strokeStyle = ringColor2;
    } else if (typeof obj._setStrokeStyles === 'function') {
      obj._setStrokeStyles(ctx, obj);
      ctx.lineWidth = widthU; // outline family: visible width ≠ native width
    } else {
      ctx.lineWidth = widthU;
      ctx.lineCap = 'round';
      ctx.strokeStyle = typeof obj.stroke === 'string' ? obj.stroke : '#000000';
    }
    ctx.lineJoin = 'round'; // offset polylines must never miter-spike
    ctx.stroke();
  } finally {
    ctx.restore();
  }
}

/** Outer rings for the CONTOUR band family (hollow/trace) — called from
 * renderContourOutline AFTER the ring-1 stroke. rings ≤ 1 (or params
 * absent) exits before touching anything: the rings=1 parity law. */
function strokeContourOuterRings(ctx, env, contours) {
  const obj = env.obj;
  let entry = null;
  try {
    entry = effectsBagOf(obj) && effectsBagOf(obj).stroke;
  } catch (_) { return; }
  const rp = entry ? ringParamsOf(entry.params) : null;
  stats.lastRingsDrawn = 0;
  if (!rp || rp.rings <= 1) return;
  const w = Number(obj.strokeWidth) || 0;
  if (!(w > 0)) return;
  const gap = rp.ringGap * RING_GAP_FACTOR * Math.max(0.0001, env.u || 0.0001);
  const deltas = [];
  for (let k = 2; k <= rp.rings; k++) deltas.push((k - 1) * (w + gap));
  const { W, H, eps } = traceWorkOf(env);
  const res = `${W}x${H}e${Math.round(eps * 1000)}`;
  const ringLoops = ensureRingLoops(
    env, contours, deltas, silhouetteSigOf(obj), res, !env.patchRegion,
  );
  if (!ringLoops) {
    stats.ringFallbacks += 1;
    return; // pending/unavailable — ring 1 only this pass (fallback law)
  }
  let drawn = 0;
  for (let i = 0; i < ringLoops.length; i++) {
    const loops = ringLoops[i];
    if (!loops || !loops.length) continue; // collapsed counters etc.
    strokeTextRingOnCtx(ctx, env, loops, i + 2, rp.ringColor2, w);
    drawn += 1;
  }
  stats.lastRingsDrawn = drawn;
}

/**
 * TRUE when the object's NATIVE-stroke band family (the Outline effect —
 * stroke slot, no contourOutline marker, no strokeFillParam, live undashed
 * stroke ink) carries rings > 1 — the seam's gate for the outer-ring pass
 * and the filledSolid variant offer. Keyed off def markers + physical
 * stroke facts, never effect ids (the effectDefs law).
 */
export function outlineRingsWanted(obj) {
  try {
    if (objectKindOf(obj) !== 'text') return false;
    const bag = effectsBagOf(obj);
    const entry = bag && bag.stroke;
    if (!entry || !entry.id) return false;
    const def = EFFECT_DEFS[entry.id];
    if (!def || !(def.slots || []).includes('stroke')) return false;
    if (def.contourOutline || def.strokeFillParam) return false;
    if (Array.isArray(obj.strokeDashArray) && obj.strokeDashArray.length) return false;
    if (!obj.stroke || !((Number(obj.strokeWidth) || 0) > 0)) return false;
    return ringParamsOf(entry.params).rings > 1;
  } catch (_) {
    return false;
  }
}

/**
 * Outer rings for the NATIVE outline family — the seam calls this AFTER the
 * die-cut closing (ring gaps must never read as pockets) and before the
 * filter chain. Ring 1 is fabric's own native stroke, already in the base —
 * untouched (parity). Returns true when at least one outer ring was drawn
 * (renderDiecutPlain keeps the offscreen on true).
 */
export function renderOutlineOuterRings(ctx, env) {
  try {
    if (!env || !env.obj || !env.frame) return false;
    const obj = env.obj;
    if (!outlineRingsWanted(obj)) return false;
    const contours = contoursFor(env);
    if (!contours || !contours.length) return false;
    const entry = effectsBagOf(obj).stroke;
    const rp = ringParamsOf(entry.params);
    const sw = Number(obj.strokeWidth) || 0;
    const outside = obj.paintFirst === 'stroke'; // the def's outside-position fact
    const wEff = outside ? sw / 2 : sw;
    const c0 = outside ? wEff / 2 : 0; // ring-1 visible band center vs the edge
    const gap = rp.ringGap * RING_GAP_FACTOR * Math.max(0.0001, env.u || 0.0001);
    const deltas = [];
    for (let k = 2; k <= rp.rings; k++) deltas.push(c0 + (k - 1) * (wEff + gap));
    const { W, H, eps } = traceWorkOf(env);
    const res = `${W}x${H}e${Math.round(eps * 1000)}`;
    const ringLoops = ensureRingLoops(
      env, contours, deltas, silhouetteSigOf(obj), res, !env.patchRegion,
    );
    stats.lastRingsDrawn = 0;
    if (!ringLoops) {
      stats.ringFallbacks += 1;
      return false; // pending/unavailable — native band only (fallback law)
    }
    let drawn = 0;
    for (let i = 0; i < ringLoops.length; i++) {
      const loops = ringLoops[i];
      if (!loops || !loops.length) continue;
      strokeTextRingOnCtx(ctx, env, loops, i + 2, rp.ringColor2, wEff);
      drawn += 1;
    }
    stats.lastRingsDrawn = drawn;
    return drawn > 0;
  } catch (e) {
    logOnce('outline outer rings failed — native band only', e);
    return false;
  }
}

/**
 * Extra outward reach (object units) the outer rings need past the band's
 * own strokeWidth/2 spill — the seam adds it to the plain-lane and geometry
 * pads (renderContourPlain / renderDiecutPlain / fxGeometry). 0 whenever
 * rings ≤ 1 (parity: the pads stay byte-identical for every existing
 * recipe). Ring widths and center steps are uniform, so the extra is
 * (rings−1)·(bandWidth + gap) for every band family.
 */
export function ringsExtraReachU(obj) {
  try {
    if (objectKindOf(obj) !== 'text') return 0; // shapes/icons ride spill()
    const bag = effectsBagOf(obj);
    const entry = bag && bag.stroke;
    if (!entry || !entry.id) return 0;
    const def = EFFECT_DEFS[entry.id];
    if (!def || !(def.slots || []).includes('stroke')) return 0;
    if (def.strokeFillParam) return 0; // sticker plate — not a ring family
    const rp = ringParamsOf(entry.params);
    if (rp.rings <= 1) return 0;
    const sw = Number(obj.strokeWidth) || 0;
    if (!(sw > 0)) return 0;
    if (!def.contourOutline) {
      // native outline family — same physical gates as outlineRingsWanted
      if (!obj.stroke) return 0;
      if (Array.isArray(obj.strokeDashArray) && obj.strokeDashArray.length) return 0;
    }
    const u = Math.max(0.0001, (obj.fontSize || 40) / 100);
    const gap = rp.ringGap * RING_GAP_FACTOR * u;
    const outside = !def.contourOutline && obj.paintFirst === 'stroke';
    const wEff = !def.contourOutline && outside ? sw / 2 : sw;
    return (rp.rings - 1) * (wEff + gap);
  } catch (_) {
    return 0;
  }
}

// ---------------------------------------------------------------- entry
/**
 * Draw the union-contour band for a contourOutline-family object onto the
 * seam's base offscreen. Call with the RAW stroke already suppressed in the
 * base pass (the seam parks strokeWidth 0 — renderBaseOffscreen).
 *
 * @param {CanvasRenderingContext2D} ctx  base offscreen ctx (identity).
 * @param {object} env  seam env: obj, frame, width, height, quality,
 *        exporting, renderBounds, patchRegion, getScratch,
 *        renderVariant('filledSolid').
 * @returns {boolean} true = band drawn; false = caller must re-render the
 *          raw-path stroke (degrade — blank text, missing variant, region
 *          bake without remembered contours, or any internal hiccup).
 */
export function renderContourOutline(ctx, env) {
  try {
    if (!env || !env.obj || !env.frame) return false;
    const contours = contoursFor(env);
    if (!contours || !contours.length) {
      stats.degrades += 1;
      return false;
    }
    strokeContoursOnCtx(ctx, env, contours);
    // Multi-ring lane: rings 2..4 outside the band. rings ≤ 1 returns before
    // touching anything (parity law); a ring hiccup must never cost the base
    // band that is already painted above.
    try {
      strokeContourOuterRings(ctx, env, contours);
    } catch (e) {
      logOnce('contour outer rings failed — base band kept', e);
    }
    return true;
  } catch (e) {
    stats.degrades += 1;
    logOnce('contour render threw — raw-path stroke drawn', e);
    return false;
  }
}

// ---------------------------------------------------------------- contourBand
// The registered fx FILTER carrying Outline/Hollow/Trace for shapes and
// icons (universal-effects U1 — the capability matrix's fx-contour cells).
//
// Band laws mirror the text defs exactly (recipe units, u = min(w,h)/100 via
// env.u, so a resize keeps proportions):
//   outline — width 0.25·thickness·u, drawn at DOUBLE width UNDER the body
//             (destination-over): the body hides the inner half, so the
//             visible band is the outer half — the same trick as text
//             outline's 'outside' position. Body KEPT.
//   hollow  — width 0.20·thickness·u, body HIDDEN (the see-through law: the
//             silhouette band is all that remains).
//   trace   — width 0.18·thickness·u, dashed [dash·u, gap·u], body HIDDEN
//             (a tracing guide, same as text trace's blanked fill).
//
// Paint comes from step params (solid color — the engine mirrors it from
// the effect entry at apply time; gradient/texture band paint is a future
// widening, documented in the matrix). DEGRADE posture: any tracer miss
// (blank silhouette, region bake without remembered contours, throw)
// returns the canvas UNTOUCHED — the plain body stays visible, hideBody is
// deliberately NOT applied without its band (never blank an object on a
// hiccup). Stock-viewer degrade: no fx seam ⇒ plain object (see the
// capability matrix's degrade notes).
const BAND_MODES = { outline: 0.25, hollow: 0.2, trace: 0.18 };

/** Step-param ring plan (multi-ring lane): the drawn outer-ring width, the
 * gap in object units and the center deltas from the silhouette edge.
 * outline's ring 1 paints at DOUBLE width under the body, so its VISIBLE
 * band is baseW wide with its center at baseW/2 — outer rings are
 * freestanding baseW-wide bands whose centers step by (wEff + gap), which
 * makes the EDGE-to-EDGE gap exactly `gap` for every mode. */
function stepRingPlanOf(p, u) {
  const factor = BAND_MODES[p.mode] || 0.25;
  const baseW = (p.thickness || 30) * factor * u;
  const wEff = p.mode === 'outline' ? baseW : Math.max(0.2 * u, baseW);
  const rp = ringParamsOf(p);
  const gap = rp.ringGap * RING_GAP_FACTOR * u;
  const c0 = p.mode === 'outline' ? baseW / 2 : 0;
  const deltas = [];
  for (let k = 2; k <= rp.rings; k++) deltas.push(c0 + (k - 1) * (wEff + gap));
  return { rp, wEff, gap, c0, deltas };
}

/** Paint-parameterized sibling of strokeContoursOnCtx: same frame mapping,
 * same round-join law, but width/color/dash from the STEP, not the object. */
function strokeContoursWithPaint(ctx, env, contours, widthU, color, dash) {
  const frame = env.frame;
  ctx.save();
  try {
    ctx.setTransform(
      frame.effX, 0, 0, frame.effY,
      -frame.x0 * frame.effX, -frame.y0 * frame.effY,
    );
    ctx.beginPath();
    for (const loop of contours) {
      ctx.moveTo(loop[0][0], loop[0][1]);
      for (let i = 1; i < loop.length; i++) ctx.lineTo(loop[i][0], loop[i][1]);
      ctx.closePath();
    }
    if (dash) ctx.setLineDash(dash);
    ctx.lineWidth = widthU;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round'; // DP polylines must never miter-spike
    ctx.strokeStyle = color;
    ctx.stroke();
  } finally {
    ctx.restore();
  }
}

registerFx({
  id: 'contourBand',
  version: 1,
  kind: 'filter',
  sanitize(p) {
    const src = p || {};
    const mode = src.mode === 'outline' || src.mode === 'hollow' || src.mode === 'trace'
      ? src.mode : null;
    const clamp = (v, lo, hi, dflt) => {
      const n = Number(v);
      return Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : dflt;
    };
    // Fixed key order — data.fx params are JSON.stringify'd into the seam
    // hash and history diffs, so the shape must be deterministic.
    const out = {
      mode,
      thickness: clamp(src.thickness, 1, 100, 30),
      color: typeof src.color === 'string' && src.color ? src.color : '#94a3b8',
      // Derived from the mode, never trusted from input: one truth for the
      // see-through law (outline keeps the body; hollow/trace hide it).
      hideBody: mode !== 'outline',
    };
    if (mode === 'trace') {
      out.dash = clamp(src.dash, 1, 100, 30);
      out.gap = clamp(src.gap, 1, 100, 25);
    }
    // Multi-ring lane: the ring trio rides ONLY when rings > 1 — a
    // rings-absent or rings:1 recipe keeps its exact params JSON (the seam
    // hash / history-diff byte law; also what keeps every pre-lane step
    // byte-stable on re-sanitize).
    const rp = ringParamsOf(src);
    if (rp.rings > 1) {
      out.rings = rp.rings;
      out.ringGap = rp.ringGap;
      out.ringColor2 = rp.ringColor2;
    }
    return out;
  },
  isEmpty(p) {
    return !p || !p.mode || !(p.thickness > 0);
  },
  // Outward reach: the band centers on the silhouette edge — half its width
  // spills out. Outline's under-body double width nets the same outer reach
  // as its full base width. +1u AA/round-cap safety. Honest over-estimate.
  // Multi-ring lane: outer rings step uniformly, so rings > 1 adds exactly
  // (rings−1)·(ringWidth + gap); rings absent returns the pre-lane number
  // byte-for-byte (margin/cache parity).
  spill(p, u) {
    if (!p || !p.mode) return 0;
    const factor = BAND_MODES[p.mode] || 0.25;
    let reach = (p.thickness || 30) * factor * u + u;
    if (p.rings > 1) {
      const plan = stepRingPlanOf(p, u);
      reach += (plan.rp.rings - 1) * (plan.wEff + plan.gap);
    }
    return reach;
  },
  // Export warm-up (fxPrimeAssets contract): a cold export's synchronous
  // render must not ship ring-1-only because the offset bake never ran.
  // Awaitable ONLY once a silhouette trace exists (the screen path or the
  // export base render owns that); without one there is nothing to offset
  // and the export honestly degrades to ring 1 (documented sliver).
  primeAssets(p, obj) {
    try {
      if (!p || !p.mode || !(p.rings > 1) || !obj) return null;
      if (ringGeoDisabled() || ringGeoBroken) return null;
      const mem = CONTOUR_MEMORY.get(obj);
      if (!mem || !mem.contours || !mem.contours.length) return null;
      const dim = typeof obj._getTransformedDimensions === 'function'
        ? obj._getTransformedDimensions({ skewX: 0, skewY: 0 }) : null;
      const w = dim ? dim.x / (obj.scaleX || 1) : Number(obj.width) || 1;
      const h = dim ? dim.y / (obj.scaleY || 1) : Number(obj.height) || 1;
      const u = Math.max(
        0.0001, obj.fontSize ? obj.fontSize / 100 : Math.min(w, h) / 100,
      );
      const plan = stepRingPlanOf(p, u);
      const key = `${mem.sil}§${plan.deltas.map((d) => d.toFixed(3)).join(',')}`;
      const cur = RING_MEMORY.get(obj);
      if (cur && cur.key === key) return cur.promise;
      kickRingBake(obj, mem.contours, plan.deltas, key, mem.res, null);
      const kicked = RING_MEMORY.get(obj);
      return kicked ? kicked.promise : null;
    } catch (_) {
      return null;
    }
  },
  applySync(canvas, env) {
    const p = env.params || {};
    if (!p.mode) return canvas;
    const contours = contoursFor(env);
    if (!contours || !contours.length) {
      stats.degrades += 1;
      return canvas; // degrade: plain body, never a blank
    }
    const W = env.width;
    const H = env.height;
    const u = Math.max(0.0001, env.u);
    const baseW = (p.thickness || 30) * (BAND_MODES[p.mode] || 0.25) * u;
    const widthU = p.mode === 'outline' ? baseW * 2 : Math.max(0.2 * u, baseW);
    const dash = p.mode === 'trace'
      ? [Math.max(0.1, (p.dash ?? 30)) * u, Math.max(0.1, (p.gap ?? 25)) * u]
      : null;
    const band = env.getScratch(W, H);
    band.ctx.setTransform(1, 0, 0, 1, 0, 0);
    band.ctx.clearRect(0, 0, W, H);
    strokeContoursWithPaint(band.ctx, env, contours, widthU, p.color || '#94a3b8', dash);
    // Multi-ring lane: outer rings paint into the SAME band scratch — the
    // hideBody composite below treats every ring as band ink (outline's
    // under-body trick hides only ring 1's inner half; outer rings lie
    // outside the body by construction, so they are always fully visible).
    // Pending/unavailable geometry draws ring 1 only (fallback law); trace's
    // dashes ride each ring's own path (fresh beginPath per ring).
    if (p.rings > 1) {
      const plan = stepRingPlanOf(p, u);
      const tw = traceWorkOf(env);
      const res = `${tw.W}x${tw.H}e${Math.round(tw.eps * 1000)}`;
      const ringLoops = ensureRingLoops(
        env, contours, plan.deltas, silhouetteSigOf(env.obj), res, !env.patchRegion,
      );
      stats.lastRingsDrawn = 0;
      if (ringLoops) {
        let drawn = 0;
        for (let i = 0; i < ringLoops.length; i++) {
          const loops = ringLoops[i];
          if (!loops || !loops.length) continue; // collapsed counter etc.
          const ringColor = (i + 2) % 2 === 0
            ? plan.rp.ringColor2 : (p.color || '#94a3b8');
          strokeContoursWithPaint(band.ctx, env, loops, plan.wEff, ringColor, dash);
          drawn += 1;
        }
        stats.lastRingsDrawn = drawn;
      } else {
        stats.ringFallbacks += 1;
      }
    }
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (p.hideBody) {
      ctx.clearRect(0, 0, W, H);
      ctx.drawImage(band.canvas, 0, 0);
    } else {
      // Band UNDER the body: existing pixels win, inner half hides — the
      // outside-outline look.
      ctx.globalCompositeOperation = 'destination-over';
      ctx.drawImage(band.canvas, 0, 0);
    }
    ctx.restore();
    return canvas;
  },
});
